import { useSession } from '@/auth/useSession';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toInitialsUppercase } from '@/lib/string-utils';
import { Link } from '@tanstack/react-router';
import { isStudent as isAirStudent } from '@airreading/common';
import { cn } from '@/lib/tailwind-utils';
import { NAV } from '@airreading/automation-test/test-ids';

export const PRIVACY_POLICY_URL = 'https://airreading.com/privacy-policy';

export const UserAvatarMenu = ({ compact = false }: { compact?: boolean }) => {
  const { actingUser: user, logout } = useSession();
  const isStudent = user ? isAirStudent(user) : false;
  const sizeClass = compact ? 'h-10 w-10' : 'h-[4rem] w-[4rem]';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn('relative rounded-full', sizeClass)}
          data-testid={NAV.userMenu}
          aria-label="Open user menu"
        >
          <Avatar
            className={cn('rounded-full', sizeClass, {
              'bg-blue-400': isStudent,
            })}
          >
            <AvatarFallback
              className={cn({
                'bg-[#fc666f] border-[#fc666f] hover:border-[#fc666f]': isStudent,
              })}
            >
              {toInitialsUppercase(user.fullName ?? '')}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 max-w-[calc(100vw-2rem)]">
        <DropdownMenuLabel className="min-w-0">
          <div className="flex min-w-0 flex-col items-center">
            <span className="max-w-full break-all text-center" data-testid={NAV.userMenuName}>
              {user.fullName}
            </span>
            <span className="max-w-full break-all text-center text-sm font-medium" data-testid={NAV.userMenuEmail}>
              {user.email}
            </span>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={isStudent ? '/account' : '/profile'} className="cursor-pointer" data-testid={NAV.userMenuProfile}>
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="cursor-pointer"
            data-testid={NAV.userMenuPrivacyPolicy}
          >
            Privacy Policy
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer" onClick={logout} data-testid={NAV.userMenuLogout}>
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
